import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'

import type { AnimatedWallpaperSize } from 'shared/wallpapers/animatedWallpaper.types'

import styles from './WallpaperLayer.module.scss'
import type { WallpaperLayerProps } from './WallpaperLayer.types'

const cssSize = (size: AnimatedWallpaperSize): string => {
  const value = `${size.value}${size.unit}`
  return size.maxPixels ? `min(${value}, ${size.maxPixels}px)` : value
}

export const WallpaperLayer = ({
  layer,
  assetBaseUrl,
  ready,
  onLoaded,
  onFailed
}: WallpaperLayerProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ready || !layer.motion || !ref.current) return
    const motion = layer.motion
    try {
      const animation = ref.current.animate(
        motion.keyframes.map(frame => ({
          offset: frame.offset,
          transform: `translate(${frame.translateX}%, ${frame.translateY}%) rotate(${frame.rotate}deg) scale(${frame.scale})`,
          ...(frame.opacity !== undefined ? { opacity: frame.opacity } : {})
        })),
        {
          duration: motion.durationMs,
          delay: -motion.phaseMs,
          easing: motion.easing,
          direction: motion.direction,
          iterations: Infinity,
          fill: 'both'
        }
      )
      return () => animation.cancel()
    } catch {
      // Unsupported animation capabilities fall back to the static poster.
      onFailed()
    }
  }, [layer.motion, ready, onFailed])

  const style: CSSProperties = {
    left: `${layer.layout.left}%`,
    top: `${layer.layout.top}%`,
    width: cssSize(layer.layout.width),
    height: layer.layout.height ? cssSize(layer.layout.height) : undefined,
    aspectRatio: layer.layout.aspectRatio,
    transformOrigin: `${layer.layout.originX}% ${layer.layout.originY}%`,
    opacity: layer.opacity,
    background: layer.type === 'gradient' ? layer.background : undefined
  }

  return (
    <div ref={ref} className={styles.layer} style={style} data-layer={layer.id}>
      {layer.type === 'image' && (
        <img
          className={styles.image}
          src={`${assetBaseUrl}${layer.src}`}
          alt=''
          decoding='async'
          style={{
            objectFit: layer.fit,
            height:
              layer.layout.height !== undefined ||
              layer.layout.aspectRatio !== undefined
                ? '100%'
                : 'auto'
          }}
          onLoad={event => {
            const image = event.currentTarget
            // Decode before revealing the scene to avoid partially drawn layers.
            void image
              .decode()
              .then(() => onLoaded(layer.id))
              .catch(onFailed)
          }}
          onError={onFailed}
        />
      )}
    </div>
  )
}
